import { Link } from "react-router";

import { ConnectButton } from "@/components/ConnectButton";
import { CountUp } from "@/components/CountUp";
import { CopyAction } from "@/components/ui";
import { useWallet } from "@/components/WalletProvider";
import { browserPublicClient as publicClient } from "@/lib/chain.client";
import {
  ASETQU_TOKEN,
  CLAIM_CONTRACT,
  NETWORK_NAME,
  NEW_TOTAL_SUPPLY,
  PERMANENT_LOCK_VAULT,
  TOKEN_SYMBOL,
  explorerAddress,
} from "@/lib/config";
import { erc20Abi, permanentLockVaultAbi, swapAbi } from "@/lib/contracts";
import { formatDisplay, shortenAddress } from "@/lib/format";
import { usePageMeta } from "@/lib/page-meta";
import { useAsync } from "@/lib/use-async";

async function readStats() {
  const result = {
    totalSwapped: null as bigint | null,
    remaining: null as bigint | null,
    walletCap: null as bigint | null,
    permanentlyLocked: null as bigint | null,
    totalSupply: null as bigint | null,
    reachable: true,
  };

  try {
    if (CLAIM_CONTRACT) {
      const [swapped, remaining, walletCap] = await Promise.all([
        publicClient.readContract({ address: CLAIM_CONTRACT, abi: swapAbi, functionName: "totalSwapped" }),
        publicClient.readContract({ address: CLAIM_CONTRACT, abi: swapAbi, functionName: "remaining" }),
        publicClient.readContract({ address: CLAIM_CONTRACT, abi: swapAbi, functionName: "WALLET_CAP" }),
      ]);
      result.totalSwapped = swapped;
      result.remaining = remaining;
      result.walletCap = walletCap;
    }
    if (PERMANENT_LOCK_VAULT) {
      result.permanentlyLocked = await publicClient.readContract({
        address: PERMANENT_LOCK_VAULT,
        abi: permanentLockVaultAbi,
        functionName: "totalPermanentlyLocked",
      });
    }
    if (ASETQU_TOKEN) {
      result.totalSupply = await publicClient.readContract({
        address: ASETQU_TOKEN,
        abi: erc20Abi,
        functionName: "totalSupply",
      });
    }
  } catch {
    result.reachable = false;
  }

  return result;
}

const MIGRATION_STEPS = [
  ["01", "HUBUNGKAN", "Hubungkan wallet yang menyimpan AsetQu Legacy."],
  ["02", "PERIKSA", "Portal membaca saldo, batas wallet, dan status kontrak."],
  ["03", "BERI IZIN", "Izinkan exact amount. Langkah ini belum memindahkan token."],
  ["04", "MIGRASIKAN", "Kirim transaksi kedua untuk menerima AsetQu baru 1:1."],
  ["05", "VERIFIKASI", "Periksa receipt, event, block, dan kontrak tujuan."],
] as const;

export function HomePage() {
  usePageMeta({
    title: "Official AsetQu Migration",
    description: "Migrasi resmi AsetQu Legacy ke token AsetQu baru dengan rasio 1:1.",
  });

  const { address } = useWallet();
  const stats = useAsync(readStats) ?? {
    totalSwapped: null,
    remaining: null,
    walletCap: null,
    permanentlyLocked: null,
    totalSupply: null,
    reachable: true,
  };

  return (
    <div className="home-page terminal-home">
      <section className="terminal-hero" aria-labelledby="home-title">
        <div className="terminal-hero__copy">
          <div className="terminal-hero__status">
            <span aria-hidden />
            OFFICIAL MIGRATION / {NETWORK_NAME.toUpperCase()}
          </div>
          <h1 id="home-title">
            MIGRASIKAN
            <br />
            ASETQU LEGACY
          </h1>
          <p className="terminal-hero__lead">
            Pindahkan AsetQu Legacy ke kontrak baru melalui proses resmi, non-custodial,
            dengan rasio migrasi 1:1 dan bukti yang dapat diperiksa on-chain.
          </p>

          <div className="terminal-hero__actions">
            {address ? (
              <Link to="/claim" className="terminal-action terminal-action--primary">
                MULAI MIGRASI <span aria-hidden>→</span>
              </Link>
            ) : (
              <ConnectButton />
            )}
            <Link to="/verify" className="terminal-action terminal-action--secondary">
              VERIFIKASI TRANSAKSI
            </Link>
          </div>

          <p className="terminal-hero__safety">
            AsetQu tidak pernah meminta seed phrase atau private key. Setiap izin token dibatasi
            ke jumlah yang Anda pilih.
          </p>
        </div>

        <aside className="official-plate" aria-label="Identitas kontrak migrasi resmi">
          <header>
            <span>ASETQU / MIGRATION AUTHORITY</span>
            <strong>{stats.reachable ? "LIVE" : "RPC CHECK"}</strong>
          </header>
          <dl>
            <div>
              <dt>NETWORK</dt>
              <dd>{NETWORK_NAME}</dd>
            </div>
            <div>
              <dt>RATIO</dt>
              <dd>1 LEGACY = 1 NEW</dd>
            </div>
            <div>
              <dt>WALLET</dt>
              <dd>{address ? shortenAddress(address, 8, 6) : "BELUM TERHUBUNG"}</dd>
            </div>
            <div className="official-plate__contract">
              <dt>OFFICIAL MIGRATION CONTRACT</dt>
              <dd>
                <code>{CLAIM_CONTRACT ?? "BELUM DIKONFIGURASI"}</code>
                {CLAIM_CONTRACT ? (
                  <span>
                    <CopyAction value={CLAIM_CONTRACT} label="Salin" />
                    <a href={explorerAddress(CLAIM_CONTRACT)} target="_blank" rel="noopener noreferrer">
                      EXPLORER ↗
                    </a>
                  </span>
                ) : null}
              </dd>
            </div>
          </dl>
          <footer>
            <span><i aria-hidden /> KONTRAK TERVERIFIKASI</span>
            <span><i aria-hidden /> EXACT APPROVAL</span>
            <span><i aria-hidden /> FIXED SUPPLY</span>
          </footer>
        </aside>
      </section>

      <section className="proof-ledger" aria-labelledby="proof-title">
        <header>
          <div>
            <p>STATUS MIGRASI</p>
            <h2 id="proof-title">Data kontrak, bukan angka promosi.</h2>
          </div>
          <span className={stats.reachable ? "is-live" : "is-warning"}>
            {stats.reachable ? "DIBACA DARI JARINGAN" : "JARINGAN BELUM TERBACA"}
          </span>
        </header>
        <dl>
          <div>
            <dt>Total supply baru</dt>
            <dd><CountUp value={stats.totalSupply ?? NEW_TOTAL_SUPPLY} /></dd>
            <small>{TOKEN_SYMBOL}</small>
          </div>
          <div>
            <dt>Batas per wallet</dt>
            <dd>{stats.walletCap === null ? "—" : <CountUp value={stats.walletCap} />}</dd>
            <small>Maksimum migrasi per alamat</small>
          </div>
          <div>
            <dt>Sudah dimigrasikan</dt>
            <dd>{stats.totalSwapped === null ? "—" : <CountUp value={stats.totalSwapped} />}</dd>
            <small>
              {stats.remaining === null ? "Pool belum dapat dibaca" : `Sisa ${formatDisplay(stats.remaining)} ${TOKEN_SYMBOL}`}
            </small>
          </div>
          <div>
            <dt>Terkunci permanen</dt>
            <dd>{stats.permanentlyLocked === null ? "—" : <CountUp value={stats.permanentlyLocked} />}</dd>
            <small>Terbukti melalui vault on-chain</small>
          </div>
        </dl>
      </section>

      <section className="migration-strip" aria-labelledby="flow-title">
        <header>
          <p>ALUR RESMI</p>
          <h2 id="flow-title">Dua tanda tangan. Satu hasil yang dapat dibuktikan.</h2>
          <p>
            Portal memisahkan izin token dan perpindahan token agar setiap keputusan dapat Anda
            periksa sebelum wallet dibuka.
          </p>
        </header>
        <ol>
          {MIGRATION_STEPS.map(([number, title, body]) => (
            <li key={number}>
              <span>{number}</span>
              <h3>{title}</h3>
              <p>{body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="verification-block" aria-labelledby="verification-title">
        <div>
          <p>BUKTI SETELAH MIGRASI</p>
          <h2 id="verification-title">Receipt sukses belum cukup jika kontraknya salah.</h2>
          <p>
            Halaman verifikasi memeriksa status receipt, alamat kontrak tujuan, event migrasi,
            wallet penerima, jumlah, dan nomor block.
          </p>
        </div>
        <Link to="/verify" className="terminal-action terminal-action--light">
          BUKA VERIFIKASI <span aria-hidden>→</span>
        </Link>
      </section>

      <section className="security-rules" aria-labelledby="security-title">
        <header>
          <p>KEAMANAN</p>
          <h2 id="security-title">Empat aturan sebelum Anda menandatangani.</h2>
        </header>
        <ol>
          <li><span>01</span><strong>ASETQU TIDAK PERNAH MEMINTA SEED PHRASE.</strong><p>Jangan masukkan kredensial wallet di website, chat, atau formulir apa pun.</p></li>
          <li><span>02</span><strong>PASTIKAN DOMAIN RESMI.</strong><p>Periksa address bar sebelum menghubungkan wallet.</p></li>
          <li><span>03</span><strong>COCOKKAN KONTRAK DI WALLET.</strong><p>Alamat penuh tersedia di setiap review transaksi.</p></li>
          <li><span>04</span><strong>IZIN HANYA EXACT AMOUNT.</strong><p>Portal tidak pernah meminta unlimited approval.</p></li>
        </ol>
        <Link to="/security">BACA PANDUAN KEAMANAN <span aria-hidden>→</span></Link>
      </section>
    </div>
  );
}
